// src/components/admin/employee/logging/AdminLogging.js
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../../../firebaseConfig"; // Removed 'auth' as we are selecting users manually
import { collection, addDoc, getDocs, query, where, Timestamp } from "firebase/firestore";
import { isRejectedStatus } from "../../../../utils/userStatus";
import "./AdminLogging.css";

// ---------- Time Helpers (Unchanged) ----------
const toMinutes = (hhmm) => {
  if (!hhmm || typeof hhmm !== "string" || !hhmm.includes(":")) return null;
  const [h, m] = hhmm.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

const toHHMM = (mins) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const getStepForType = (type) => (type === "admin" ? 5 : 10);
const ceilToStep = (mins, step) => Math.ceil(mins / step) * step;

const getDayBounds = (dateStr) => {
  if (!dateStr) return { startMin: 0, endMin: 0, label: "" };

  const [y, m, d] = dateStr.split("-").map(Number);
  const dayOfWeek = new Date(y, m - 1, d).getDay(); 
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

  const startMin = isWeekend ? 8 * 60 + 30 : 14 * 60;
  const endMin = isWeekend ? 18 * 60 : 22 * 60 + 30;

  const label = isWeekend ? "8:30 AM – 6:00 PM" : "2:00 PM – 10:30 PM";
  return { startMin, endMin, label };
};

const getOptionsForDate = (dateStr, step) => {
  if (!dateStr) return [];
  const { startMin, endMin } = getDayBounds(dateStr);

  const options = [];
  for (let mins = startMin; mins <= endMin; mins += step) {
    const hours = Math.floor(mins / 60);
    const m = mins % 60;

    const value = `${String(hours).padStart(2, "0")}:${String(m).padStart(2, "0")}`;

    const ampm = hours >= 12 ? "PM" : "AM";
    const h12 = hours % 12 || 12;
    const label = `${h12}:${String(m).padStart(2, "0")} ${ampm}`;

    options.push({ value, label });
  }
  return options;
};

const sanitizeEntriesForDate = (rawEntries, dateStr) => {
  const { startMin, endMin } = getDayBounds(dateStr);
  const next = rawEntries.map((e) => ({ ...e }));

  for (let i = 0; i < next.length; i++) {
    const step = getStepForType(next[i].type);
    const prevEndMin = i > 0 ? toMinutes(next[i - 1].endTime) : null;
    let minStartAllowed = Math.max(startMin, prevEndMin ?? startMin);
    minStartAllowed = ceilToStep(minStartAllowed, step);

    const sRaw = toMinutes(next[i].startTime);
    if (sRaw != null) {
      let s = Math.max(sRaw, minStartAllowed);
      s = ceilToStep(s, step);
      if (s > endMin) next[i].startTime = "";
      else next[i].startTime = toHHMM(s);
    }

    const startNowMin = toMinutes(next[i].startTime);
    const eRaw = toMinutes(next[i].endTime);

    if (eRaw != null) {
      const baseMinEnd = (startNowMin != null ? startNowMin : minStartAllowed) + step;
      const minEndAllowed = ceilToStep(baseMinEnd, step);
      let e = ceilToStep(eRaw, step);

      if (e < minEndAllowed) next[i].endTime = "";
      else if (e > endMin) next[i].endTime = "";
      else next[i].endTime = toHHMM(e);
    }

    const s2 = toMinutes(next[i].startTime);
    const e2 = toMinutes(next[i].endTime);
    if (s2 != null && e2 != null && e2 <= s2) next[i].endTime = "";

    const endNowMin = toMinutes(next[i].endTime);
    if (endNowMin != null && next[i + 1]) {
      const nextStep = getStepForType(next[i + 1].type);
      let minNextStart = Math.max(startMin, endNowMin);
      minNextStart = ceilToStep(minNextStart, nextStep);

      const nextStartMin = toMinutes(next[i + 1].startTime);
      if (nextStartMin == null || nextStartMin < minNextStart) {
        next[i + 1].startTime = toHHMM(minNextStart);
        const nextEndMin = toMinutes(next[i + 1].endTime);
        if (nextEndMin != null && nextEndMin <= minNextStart) next[i + 1].endTime = "";
      }
    }
  }
  return next;
};

// ---------- Component ----------
const AdminLogging = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Admin Controls
  const [tutorsList, setTutorsList] = useState([]);
  const [statusFilter, setStatusFilter] = useState("active"); // Default active
  const [selectedTutorId, setSelectedTutorId] = useState("");

  const [userData, setUserData] = useState({
    name: "",
    adminRate: 0,
    teachingRate: 0,
  });

  const [date, setDate] = useState(() => {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    const localDate = new Date(now.getTime() - offset * 60 * 1000);
    return localDate.toISOString().split("T")[0]; // "YYYY-MM-DD" for the <input type="date">
  });

  const [entries, setEntries] = useState([
    { type: "admin", startTime: "", endTime: "", startMode: "select", endMode: "select" },
  ]);

  // 1. Fetch All Tutors on Mount
  useEffect(() => {
    const fetchTutors = async () => {
      try {
        // Query users collection for role == 'tutor'
        const q = query(collection(db, "users"), where("role", "==", "tutor"));
        const snapshot = await getDocs(q);
        const list = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        .filter((tutor) => !isRejectedStatus(tutor.status));
        setTutorsList(list);
      } catch (error) {
        console.error("Error fetching tutors:", error);
        alert("Failed to load tutor list.");
      } finally {
        setLoading(false);
      }
    };
    fetchTutors();
  }, []);

  // 2. Derive filtered list based on status
  const filteredTutors = useMemo(() => {
    if (statusFilter === "All") return tutorsList;
    return tutorsList.filter((t) => (t.status || "").toLowerCase() === statusFilter.toLowerCase());
  }, [tutorsList, statusFilter]);

  // 3. Update userData when a tutor is selected
  useEffect(() => {
    if (!selectedTutorId) {
      setUserData({ name: "", adminRate: 0, teachingRate: 0 });
      return;
    }
    const tutor = tutorsList.find((t) => t.id === selectedTutorId);
    if (tutor) {
      setUserData({
        name: tutor.name,
        adminRate: Number(tutor.adminRate) || 0,
        teachingRate: Number(tutor.teachingRate) || 0,
      });
    }
  }, [selectedTutorId, tutorsList]);

  // Select the first tutor in the filtered list automatically if current selection is invalid
  useEffect(() => {
    if (!loading && filteredTutors.length > 0) {
      // Check if current selectedId is still in the filtered list
      const exists = filteredTutors.find((t) => t.id === selectedTutorId);
      if (!exists) {
        setSelectedTutorId(filteredTutors[0].id);
      }
    } else if (!loading && filteredTutors.length === 0) {
      setSelectedTutorId("");
    }
  }, [filteredTutors, loading, selectedTutorId]); // Removed selectedTutorId from deps to avoid loop

  const dayBounds = useMemo(() => getDayBounds(date), [date]);

  // Options per type
  const optionsAdmin = useMemo(() => getOptionsForDate(date, 5), [date]);
  const optionsTeaching = useMemo(() => getOptionsForDate(date, 10), [date]);
  const getOptionsForType = (type) => (type === "admin" ? optionsAdmin : optionsTeaching);

  // Re-sanitize when date changes
  useEffect(() => {
    setEntries((prev) => sanitizeEntriesForDate(prev, date));
  }, [date]);

  const getStartOptionsForRow = (rowIndex, draftEntries) => {
    const row = draftEntries[rowIndex];
    const step = getStepForType(row.type);
    const options = getOptionsForType(row.type);

    const prevEndMin = rowIndex > 0 ? toMinutes(draftEntries[rowIndex - 1].endTime) : null;
    let minStart = Math.max(dayBounds.startMin, prevEndMin ?? dayBounds.startMin);
    minStart = ceilToStep(minStart, step);

    return options.filter((t) => toMinutes(t.value) >= minStart);
  };

  const getEndOptionsForRow = (rowIndex, draftEntries) => {
    const row = draftEntries[rowIndex];
    const step = getStepForType(row.type);
    const options = getOptionsForType(row.type);

    const prevEndMin = rowIndex > 0 ? toMinutes(draftEntries[rowIndex - 1].endTime) : null;
    let minStartAllowed = Math.max(dayBounds.startMin, prevEndMin ?? dayBounds.startMin);
    minStartAllowed = ceilToStep(minStartAllowed, step);

    const startMin = toMinutes(row.startTime);
    const baseMinEnd = (startMin != null ? startMin : minStartAllowed) + step;
    const minEnd = ceilToStep(baseMinEnd, step);

    return options.filter((t) => toMinutes(t.value) >= minEnd);
  };

  const handleEntryChange = (index, field, value) => {
    setEntries((prev) => {
      const next = prev.map((e) => ({ ...e }));

      if (field === "startTime" && value === "CUSTOM") {
        next[index].startMode = "manual";
        next[index].startTime = "";
        next[index].endTime = ""; 
        return next;
      }

      if (field === "endTime" && value === "CUSTOM") {
        next[index].endMode = "manual";
        next[index].endTime = "";
        return next;
      }

      next[index][field] = value;

      if (field === "startTime") {
        const s = toMinutes(next[index].startTime);
        const e = toMinutes(next[index].endTime);
        if (s != null && e != null && e <= s) next[index].endTime = "";
      }

      if (field === "endTime") {
        const endNowMin = toMinutes(next[index].endTime);
        if (endNowMin != null && next[index + 1]) {
          const nextStep = getStepForType(next[index + 1].type);
          let minNextStart = Math.max(dayBounds.startMin, endNowMin);
          minNextStart = ceilToStep(minNextStart, nextStep);

          const nextStartMin = toMinutes(next[index + 1].startTime);
          if (nextStartMin == null || nextStartMin < minNextStart) {
            next[index + 1].startTime = toHHMM(minNextStart);

            const nextEndMin = toMinutes(next[index + 1].endTime);
            if (nextEndMin != null && nextEndMin <= minNextStart) next[index + 1].endTime = "";
          }
        }
      }

      return sanitizeEntriesForDate(next, date);
    });
  };

  const toggleMode = (index, which, mode) => {
    setEntries((prev) => {
      const next = prev.map((e) => ({ ...e }));
      if (which === "start") {
        next[index].startMode = mode;
        next[index].startTime = "";
        next[index].endTime = "";
      } else {
        next[index].endMode = mode;
        next[index].endTime = "";
      }
      return sanitizeEntriesForDate(next, date);
    });
  };

  const addEntry = () => {
    setEntries((prev) => {
      const last = prev[prev.length - 1];
      const lastEnd = last?.endTime || "";
      const next = [
        ...prev,
        { type: "admin", startTime: lastEnd, endTime: "", startMode: "select", endMode: "select" },
      ];
      return sanitizeEntriesForDate(next, date);
    });
  };

  const removeEntry = (index) => {
    setEntries((prev) => sanitizeEntriesForDate(prev.filter((_, i) => i !== index), date));
  };

  const handleSubmit = async () => {
    if (!selectedTutorId) {
      alert("Please select a tutor first.");
      return;
    }
    if (entries.some((e) => !e.startTime || !e.endTime)) {
      alert("Please enter both start and end times for all rows.");
      return;
    }

    // Confirmation message mentions the tutor name
    if (!window.confirm(`Are you sure you want to submit these hours for ${userData.name}?`)) return;

    setSubmitting(true);

    try {
      const batchPromises = entries.map(async (entry) => {
        const startTs = Timestamp.fromDate(new Date(`${date} ${entry.startTime}`));
        const endTs = Timestamp.fromDate(new Date(`${date} ${entry.endTime}`));

        const durationMs = endTs.toDate() - startTs.toDate();
        const rawHours = durationMs / (1000 * 60 * 60);

        if (rawHours <= 0) throw new Error("End time must be after start time");

        const roundedHours = Math.round(rawHours * 100) / 100;
        const rateUsed = entry.type === "admin" ? userData.adminRate : userData.teachingRate;
        const totalPay = parseFloat((roundedHours * rateUsed).toFixed(2));

        // Inside handleSubmit, change the date field:
        const [y, m, d] = date.split("-");
        const formattedDate = `${m}/${d}/${y}`; // Converts YYYY-MM-DD to MM/DD/YYYY

        const record = {
          uid: selectedTutorId, // Use the selected tutor's UID
          tutorName: userData.name, // Use the selected tutor's Name
          date: formattedDate,
          type: entry.type,
          startTime: startTs,
          endTime: endTs,
          hours: roundedHours,
          rateUsed: rateUsed,
          totalPay: totalPay,
          status: "approved",
          source: "admin_logging", // Distinguish source
        };

        return addDoc(collection(db, "payroll_records"), record);
      });

      await Promise.all(batchPromises);

      alert(`✅ Hours submitted successfully for ${userData.name}!`);
      setEntries([
        { type: "admin", startTime: "", endTime: "", startMode: "select", endMode: "select" },
      ]);
    } catch (error) {
      console.error("Error submitting:", error);
      alert("Error submitting hours. Please check times.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="tl-page">
        <div className="tl-card">
          <div className="tl-loading">Loading Tutors…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="tl-page">
      <div className="tl-card">
        <div className="tl-header">
          <div>
            <h2 className="tl-title">Admin Log Hours</h2>
            <p className="tl-subtitle">
              Manually log time blocks for a tutor.
            </p>
          </div>

          {/* New Header Controls: Status & Tutor Selector */}
          <div className="tl-headerControls">
            <div className="tl-controlGroup">
              <label className="tl-userLabel">Status</label>
              <select 
                className="tl-headerSelect"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="active">Active</option>
                <option value="on hold">On Hold</option>
                <option value="finished">Finished</option>
                <option value="All">All</option>
              </select>
            </div>

            <div className="tl-controlGroup">
              <label className="tl-userLabel">Tutor</label>
              <select 
                className="tl-headerSelect"
                value={selectedTutorId}
                onChange={(e) => setSelectedTutorId(e.target.value)}
                disabled={filteredTutors.length === 0}
              >
                {filteredTutors.length === 0 ? (
                  <option value="">No tutors found</option>
                ) : (
                  filteredTutors.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>
        </div>

        <div className="tl-dateCard">
          <div className="tl-dateRow">
            <div>
              <label className="tl-label">Date</label>
              <div className="tl-help">Allowed range: {dayBounds.label}</div>
            </div>

            <input
              className="tl-dateInput"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
        </div>

        <div className="tl-table">
          <div className="tl-tableHead">
            <div className="tl-colType">Type</div>
            <div className="tl-colTime">Start</div>
            <div className="tl-colTime">End</div>
            <div className="tl-colAction" />
          </div>

          {entries.map((entry, index) => {
            const rowStep = getStepForType(entry.type);
            const rowStepSeconds = rowStep * 60;

            const startOptions = getStartOptionsForRow(index, entries);
            const endOptions = getEndOptionsForRow(index, entries);

            const prevEndMin = index > 0 ? toMinutes(entries[index - 1].endTime) : null;
            let minStartAllowed = Math.max(dayBounds.startMin, prevEndMin ?? dayBounds.startMin);
            minStartAllowed = ceilToStep(minStartAllowed, rowStep);

            const minStartHHMM = toHHMM(minStartAllowed);
            const maxHHMM = toHHMM(dayBounds.endMin);

            const startNowMin = toMinutes(entry.startTime);
            const minEndAllowedMin = ceilToStep(
              (startNowMin != null ? startNowMin : minStartAllowed) + rowStep,
              rowStep
            );
            const minEndHHMM = toHHMM(Math.min(minEndAllowedMin, dayBounds.endMin));

            const startDisabled = startOptions.length === 0 && entry.startMode === "select";
            const endDisabled = !entry.startTime && entry.endMode === "select";

            return (
              <div className={`tl-row ${index % 2 === 1 ? "isAlt" : ""}`} key={index}>
                {/* Type */}
                <div className="tl-field tl-fieldType">
                  <label className="tl-labelSmall">Type</label>
                  <select
                    className="tl-select"
                    value={entry.type}
                    onChange={(e) => handleEntryChange(index, "type", e.target.value)}
                  >
                    <option value="admin">Admin</option>
                    <option value="teaching">Teaching</option>
                  </select>
                  <div className="tl-microHelp">&nbsp;</div>
                </div>

                {/* Start */}
                <div className="tl-field">
                  <label className="tl-labelSmall">Start Time</label>
                  {entry.startMode === "select" ? (
                    <select
                      className="tl-select"
                      value={entry.startTime}
                      onChange={(e) => handleEntryChange(index, "startTime", e.target.value)}
                      disabled={startDisabled}
                    >
                      <option value="">{startDisabled ? "No valid start times" : "Select…"}</option>
                      <option value="CUSTOM">Custom time…</option>
                      <option disabled>──────────</option>
                      {startOptions.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="tl-inline">
                      <input
                        className="tl-timeInput"
                        type="time"
                        step={rowStepSeconds}
                        min={minStartHHMM}
                        max={maxHHMM}
                        value={entry.startTime}
                        onChange={(e) => handleEntryChange(index, "startTime", e.target.value)}
                        autoFocus
                      />
                      <button
                        className="tl-ghostBtn"
                        onClick={() => toggleMode(index, "start", "select")}
                        type="button"
                        title="Back to dropdown"
                      >
                        Back
                      </button>
                    </div>
                  )}

                  <div className="tl-microHelp">
                    {index > 0
                      ? `Must be ≥ previous end (${toHHMM(minStartAllowed)})`
                      : `Starts at ${toHHMM(dayBounds.startMin)}`}
                  </div>
                </div>

                {/* End */}
                <div className="tl-field">
                  <label className="tl-labelSmall">End Time</label>
                  {entry.endMode === "select" ? (
                    <select
                      className="tl-select"
                      value={entry.endTime}
                      onChange={(e) => handleEntryChange(index, "endTime", e.target.value)}
                      disabled={endDisabled}
                    >
                      <option value="">{endDisabled ? "Select a start time first" : "Select…"}</option>
                      <option value="CUSTOM">Custom time…</option>
                      <option disabled>──────────</option>
                      {!endDisabled &&
                        endOptions.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                    </select>
                  ) : (
                    <div className="tl-inline">
                      <input
                        className="tl-timeInput"
                        type="time"
                        step={rowStepSeconds}
                        min={minEndHHMM}
                        max={maxHHMM}
                        value={entry.endTime}
                        onChange={(e) => handleEntryChange(index, "endTime", e.target.value)}
                        autoFocus
                      />
                      <button
                        className="tl-ghostBtn"
                        onClick={() => toggleMode(index, "end", "select")}
                        type="button"
                        title="Back to dropdown"
                      >
                        Back
                      </button>
                    </div>
                  )}
                  <div className="tl-microHelp">End must be after start.</div>
                </div>

                {/* Remove */}
                <div className="tl-action">
                  {entries.length > 1 ? (
                    <button
                      className="tl-dangerBtn"
                      onClick={() => removeEntry(index)}
                      type="button"
                      title="Remove this row"
                    >
                      Remove
                    </button>
                  ) : (
                    <div />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="tl-actions">
          <button className="tl-secondaryBtn" onClick={addEntry} type="button">
            + Add Another Block
          </button>

          <button className="tl-primaryBtn" onClick={handleSubmit} disabled={submitting || !selectedTutorId} type="button">
            {submitting ? "Submitting…" : "Submit Log"}
          </button>

          <div className="tl-footnote">
            Tip: End Time automatically sets the next block’s Start Time to prevent overlaps.
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogging;
